import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { careerLayout, careerType } from 'services/careerType';
import { ICareerSkillGraph } from 'core/hooks/CareerSkillGraph';

/**
 * Плашка «это предпросмотр, а не твоя цель» (ТЗ 0071, пп. 6.1 и 6.6).
 *
 * Два случая — одна формулировка: цель не выбрана вовсе (сервер подставил
 * первую профессию) или смотрится чужая профессия. В обоих карта показывает
 * «чего не хватает» для профессии, которую человек своей не называл, и экран
 * обязан это признать, а не выдавать подстановку за выбор.
 *
 * 🔴 Условие — по флагам СЕРВЕРА (`trackIsDefault`, `isChosenGoal`), а не по
 * сравнению кода со списком профессий. Прежняя плашка ждала, пока приедет
 * список, и молчала, если он не приехал; сервер же знает цель в том же ответе,
 * где отдаёт карту.
 *
 * 🔴 Просмотр другой профессии цель НЕ меняет: выбор — только кнопкой, и
 * только через диалог подтверждения (`useChooseGoalDialog`), потому что смена
 * цели перестраивает план, рекомендации и подбор вакансий.
 *
 * В предпросмотре методиста кнопки нет: мутацию сервер отобьёт, а кнопка,
 * которая заведомо не сработает, хуже отсутствующей.
 */
export const showsGoalPreview = (
  graph: Pick<ICareerSkillGraph, 'trackIsDefault' | 'isChosenGoal'>,
): boolean => graph.trackIsDefault || !graph.isChosenGoal;

interface ISkillMapGoalBanner {
  graph: Pick<ICareerSkillGraph, 'trackIsDefault' | 'isChosenGoal'>;
  /** Можно ли выбрать цель отсюда: у методиста в предпросмотре — нет. */
  canChoose: boolean;
  onChoose: () => void;
  /** Своя цель есть, а смотрится другая — выход «к своей карте». */
  onBackToGoal?: () => void;
}

export const SkillMapGoalBanner = memo(
  ({ graph, canChoose, onChoose, onBackToGoal }: ISkillMapGoalBanner) => {
    const { t } = useTranslation();
    if (!showsGoalPreview(graph)) return null;

    return (
      <Root role="status" data-testid="skm-goal-preview">
        <Text>{t('careerCenter.map.preview.text')}</Text>
        <Actions>
          {canChoose && (
            <Primary type="button" onClick={onChoose}>
              {t('careerCenter.map.preview.choose')}
            </Primary>
          )}
          {onBackToGoal && (
            <Secondary type="button" onClick={onBackToGoal}>
              {t('careerCenter.map.backToGoalShort')}
            </Secondary>
          )}
        </Actions>
      </Root>
    );
  },
);

const Root = styled.div`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  padding: 8px 12px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background: ${Colors.bg_gray};
`;

const Text = styled.p`
  ${careerType.bodyStrong()};
  margin: 0;
  min-width: 0;
  color: ${Colors.black};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/**
 * Жёлтый — только ФОН главной кнопки, текст на нём — `btn_text`: жёлтый как
 * цвет текста не проходит контраст ни в одной теме.
 */
const Primary = styled.button`
  ${careerType.bodyStrong()};
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  cursor: pointer;
`;

const Secondary = styled.button`
  ${careerType.bodyStrong()};
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: 1px solid ${Colors.black};
  border-radius: 10px;
  background: ${Colors.white};
  color: ${Colors.black};
  cursor: pointer;
`;
